/**
 * @file Pure business logic for existing-site flow — zero DOM dependency.
 *
 * During Vue migration:
 *   - resetAppState  → useBriefStore().reset() + useBuildStore().reset() + ...
 *   - pickPathTarget → router.push() based on return value
 *   - buildKeepNote / buildRefreshNote / buildRebrandNote already in prompts/existing.js
 */
import type { AppState, Brief, Page, Uploads } from '../types.ts';

// ── INITIAL STATE FACTORIES ────────────────────────────────────────

/** Create a fresh Brief object. */
export const createEmptyBrief = (): Brief => ({
  businessDesc: '',
  customerProblem: '',
  desiredAction: '',
  actionDetail: '',
  leadMagnet: '',
  vibes: [],
  refsite: '',
});

/** Create a fresh home Page. */
export const createHomePage = (): Page => ({
  id: 'page-home',
  name: 'Home',
  sects: [],
  shellHtml: '',
  shellStatus: 'pending',
  assembled: '',
  editHist: [],
  editUndoStack: [],
  chatLog: [],
});

/** Create empty Uploads. */
export const createEmptyUploads = (): Uploads => ({
  docs: [],
  images: [],
});

// ── STATE RESET ────────────────────────────────────────────────────

/**
 * Return a partial AppState that resets all user-generated data.
 * Caller applies this to their state store.
 *
 * Does NOT touch: apiKey, cfToken, cfProject, cfAccountId, notificationEmail
 * (those are persisted settings, not session data).
 */
export const getResetState = (): Partial<AppState> => ({
  brief: createEmptyBrief(),
  briefStep: 0,
  followUps: [],
  enrichment: {},
  logoImg: null,
  logoUrl: '',
  inspoImg: null,
  concept: null,
  conceptContext: '',
  chatImage: null,
  chatTab: 'sections',
  designTab: 'brand',
  chatTargetSection: null,
  inlineEditMode: false,
  stockImages: [],
  pages: [createHomePage()],
  activePage: 0,
  expanded: null,
  running: false,
  editInput: '',
  isEditing: false,
  startPath: null,
  existingUrl: '',
  existingScrape: null,
  existingOpt: 'keep',
  extractedFacts: null,
  factsConfirmed: false,
  editingFact: null,
  purchasedFont: '',
  brandGuideImg: null,
  marketingBrief: null,
  copywritingBrief: null,
  uploads: createEmptyUploads(),
  docNotes: '',
  activeDocIdx: 0,
  pickerOpen: false,
  dragSrcIdx: null,
});

// ── PATH ROUTING ───────────────────────────────────────────────────

export type OnboardingPath = 'questions' | 'existing' | 'docs';

/**
 * Map an onboarding path choice to a screen name.
 * Pure mapping — caller handles navigation.
 */
export const pathToScreen = (path: OnboardingPath): string => {
  const map: Record<OnboardingPath, string> = {
    questions: 'brief',
    existing: 'existing',
    docs: 'upload',
  };
  return map[path];
};

// ── EXISTING SITE OPTION ───────────────────────────────────────────

export type ExistingOption = 'keep' | 'refresh' | 'rebrand';

export const EXISTING_OPTIONS: ExistingOption[] = ['keep', 'refresh', 'rebrand'];

/** Validate rebrand direction has enough content. */
export const isRebrandReady = (directionText: string): boolean => directionText.trim().length >= 20;

/** Get option config for the existing-site detail screen. */
export const getExOptionConfig = (opt: ExistingOption) => {
  const configs: Record<ExistingOption, { cls: string; label: string; h1: string; lead: string }> =
    {
      keep: {
        cls: 'chip-fast',
        label: 'Fastest path',
        h1: 'A few visual preferences',
        lead: 'These help shape the look and feel. All optional — skip straight to generate if you prefer.',
      },
      refresh: {
        cls: 'chip-smart',
        label: 'Content refresh',
        h1: 'Help us write better copy',
        lead: 'The more context you give, the more targeted the rewritten content will be. All optional.',
      },
      rebrand: {
        cls: 'chip-bold',
        label: 'Full rebrand',
        h1: 'Tell us where you are going',
        lead: 'The direction note is required — without it the AI has no signal for the new brand. The rest is optional.',
      },
    };
  return configs[opt];
};
